import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;

  handleClose(): void;
}

const CampaignStatusDescriptionsDialog: React.FC<Props> = ({ open, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'lg'}>
      <DialogContent className={classes.content}>
        <Table className={classes.table} size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '225px' }}>{t('status')}</TableCell>
              <TableCell>{t('explanation')}</TableCell>
              <TableCell>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'working')}>
                  <Typography className={classes.statusText}>{t('campaignIsWorking')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusRunningDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'break')}>
                  <Typography className={classes.statusText}>{t('campaignTakesBreak')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusPausedDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'turnedOff')}>
                  <Typography className={classes.statusText}>{t('campaignTurnedOff')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusSuspendedDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'needsHelp')}>
                  <Typography className={classes.statusText}>{t('campaignNeedsHelp')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusNeedsHelpDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'done')}>
                  <Typography className={classes.statusText}>{t('campaignDone')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusFinishedDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'archived')}>
                  <Typography className={classes.statusText}>{t('campaignArchived')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusArchivedDescription')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <div className={cx(classes.status, 'draft')}>
                  <Typography className={classes.statusText}>{t('campaignDraft')}</Typography>
                </div>
              </TableCell>
              <TableCell>{t('campaignStatusCreatedDescription')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  content: {
    padding: '0 0 24px 0 !important',
    whiteSpace: 'pre-wrap',
  },
  table: {
    '& th, & td': {
      fontSize: theme.typography.pxToRem(15),
      padding: '12px 20px',
      backgroundColor: '#FFF',
    },
    '& th': {
      verticalAlign: 'middle',
    },
    '& tr': {
      verticalAlign: 'top',
      '& td': {
        color: theme.palette.greys.medium,
      },
      '&:last-of-type td': {
        borderBottom: 'none',
      },
    },
  },
  status: {
    borderRadius: '10px',
    width: 'fit-content',
    padding: '0 8px',
    '&.working': {
      background: theme.palette.backgrounds.lightGreen,
    },
    '&.break': {
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.needsHelp': {
      background: theme.palette.backgrounds.lightRed,
    },
    '&.done': {
      background: theme.palette.backgrounds.lightViolet,
    },
    '&.archived': {
      background: theme.palette.greys.light,
    },
    '&.draft': {
      background: theme.palette.greys.lighter,
    },
    '&.turnedOff': {
      background: theme.palette.backgrounds.lightOrange,
    },
  },
  statusText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.primary,
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
}));

export default CampaignStatusDescriptionsDialog;
